/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import humanize from 'humanize-plus';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveNetwork } from '@nivo/network';

import { CIColors, CIFonts } from 'shared';

import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { ChartDimensions } from '../../../../constants/chartConfig';
import { Namespaces } from '../../../../i18n';
import {
  ClusterNodeListResponsePayload,
  ClusterNodeListRole,
} from '../../../../types/cluster/ClusterNodeListResponsePayload';
import { determineNodeStatusColor } from '../../../../utils/determineNodeStatusColor';
import { NodeStatus } from '../NodeStatus';
import { NodesNetworkCustomNode } from './components/NodesNetworkCustomNode';
import { NodesNetworkTooltip } from './components/NodesNetworkTooltip';
import { NetworkLink, NetworkNode } from './NodesNetwork.types';

type NodesNetworkProps = {
  nodes: ClusterNodeListResponsePayload[];
  updateReplicas: (replicas: number) => void;
  isLoading: boolean;
  dimensions?: ChartDimensions;
};

export const NodesNetwork = memo<NodesNetworkProps>(props => {
  const { nodes, updateReplicas, isLoading, dimensions = { width: 240, height: 240 } } = props;

  const { t } = useTranslation(Namespaces.Cluster);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const multipleNodes = useMemo(() => nodes.length > 2, [nodes]);

  const getNodeLabel = useCallback(
    (id: string, role: ClusterNodeListRole) => {
      const label =
        role === ClusterNodeListRole.primary
          ? t('details.replicas.roles.primary')
          : t('details.replicas.roles.replica');
      return role === ClusterNodeListRole.primary ? label : `${label} #${id}`;
    },
    [t],
  );

  const data = useMemo(() => {
    const tmpNetworkNodesData: NetworkNode[] = [];
    const tmpNetworkLinksData: NetworkLink[] = [];
    let nodeId = 1;

    nodes.sort((a, b) => {
      if (a.role === ClusterNodeListRole.primary && b.role !== ClusterNodeListRole.primary) return -1;
      if (a.role !== ClusterNodeListRole.primary && b.role === ClusterNodeListRole.primary) return 1;
      return 0;
    });

    const primaryNode = nodes.filter(node => node.role === ClusterNodeListRole.primary)[0];
    const replicaNodes = nodes.filter(node => node.role !== ClusterNodeListRole.primary);

    if (primaryNode) {
      tmpNetworkNodesData.push({
        id: nodeId.toString(),
        label: getNodeLabel(nodeId.toString(), primaryNode.role),
        status: primaryNode.status,
        size: 32,
        color: CIColors.Primary,
        type: 'primary',
        storage: primaryNode.storage,
      });
    }

    replicaNodes.map(n => {
      nodeId++;
      tmpNetworkNodesData.push({
        id: nodeId.toString(),
        label: getNodeLabel(nodeId.toString(), n.role),
        status: n.status,
        size: 24,
        color: CIColors.Secondary,
        type: 'replica',
        storage: n.storage,
      });
    });

    tmpNetworkNodesData.push({
      id: 'add',
      label: t('details.replicas.add_replica.title'),
      status: 'None' as NodeStatus,
      size: 24,
      color: CIColors.GraphicElements,
      type: 'add',
      onClick: () => setIsConfirmOpen(true),
    });

    tmpNetworkNodesData.map(n =>
      tmpNetworkLinksData.push({
        source: tmpNetworkNodesData[0].id,
        target: n.id,
        distance: 80,
      }),
    );

    return { nodes: tmpNetworkNodesData, links: tmpNetworkLinksData };
  }, [nodes, getNodeLabel, t]);

  const onConfirmAddReplica = () => {
    const currentReplicas = nodes.filter(n => n.role !== ClusterNodeListRole.primary).length;
    updateReplicas(currentReplicas + 1);
    setIsConfirmOpen(false);
  };

  return (
    <HUSpin spinning={isLoading}>
      <div style={{ display: 'flex', alignItems: 'center', overflow: 'visible' }}>
        <div style={{ flex: '0 0 auto', width: `${dimensions.width}px`, height: `${dimensions.height}px` }}>
          <ResponsiveNetwork
            repulsivity={multipleNodes ? 100 : undefined}
            centeringStrength={multipleNodes ? 0.3 : undefined}
            data={data}
            linkDistance={t => t.distance}
            linkThickness={t => t.source.data.size / 7}
            linkColor={t => (t.target.id === 'add' ? CIColors.GraphicElements : t.source.color)}
            nodeSize={t => t.size}
            activeNodeSize={t => 1.5 * t.size}
            inactiveNodeSize={t => 0.7 * t.size}
            nodeColor={t => t.color}
            linkBlendMode="multiply"
            onClick={node => node.data.onClick?.()}
            nodeTooltip={t => (
              <NodesNetworkTooltip label={t.node.data.label} status={t.node.data.status} type={t.node.data.type} />
            )}
            nodeComponent={props => <NodesNetworkCustomNode {...props} />}
            motionConfig="gentle"
          />
        </div>
        <div
          style={{
            marginLeft: 32,
            display: 'flex',
            flexFlow: 'column',
            direction: 'ltr',
            width: '130px',
            height: '250px',
            willChange: 'auto',
            overflow: 'auto',
            gap: '3px',
          }}
        >
          {data.nodes
            .filter(n => n.type !== 'add')
            .map(n => (
              <div key={n.id} style={{ display: 'flex', flexFlow: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: determineNodeStatusColor(n.status),
                      marginRight: '5px',
                    }}
                  />
                  <span
                    style={{
                      color: CIColors.Text,
                      fontWeight: 400,
                      fontSize: 14,
                      fontFamily: CIFonts.Text,
                      marginRight: 8,
                    }}
                  >
                    {n.label}
                  </span>
                </div>
                {n.storage && (
                  <span
                    style={{
                      color: CIColors.GraphicElements,
                      fontSize: 10,
                      fontFamily: CIFonts.Text,
                      marginLeft: '12px',
                    }}
                  >
                    {t('details.replicas.storage_usage', {
                      used: humanize.fileSize(n.storage.usedRaw * Math.pow(1024, 2), 0),
                      usedPercent: parseInt(n.storage.usedPercent),
                      initial: humanize.fileSize((n.storage.usedRaw + n.storage.available) * Math.pow(1024, 2), 0),
                    })}
                  </span>
                )}
              </div>
            ))}
        </div>
        <SimpleConfirmModal
          open={isConfirmOpen}
          message={t('details.replicas.add_replica.confirm')}
          onCancel={() => setIsConfirmOpen(false)}
          onConfirm={onConfirmAddReplica}
        />
      </div>
    </HUSpin>
  );
});
